<script setup lang="ts" generic="T extends Array<Record<string, any>>">
const windowScroll = useWindowScroll();
const device = useDevice();

const props = defineProps<{ items: T }>();
const parent = useTemplateRef('parent');
const imgs = useTemplateRef('imgs');
const offset = useElementBounding(parent);
const activeItem = ref<typeof props.items[number]>();
const topIndicate = 132;

const startFixing = computed(() => {
    if (
        !windowScroll.y 
        || !device.isDesktop.value
        || !imgs.value 
        || !offset.bottom.value 
        || !offset.top.value
    ) return;

    return offset.bottom.value > imgs.value.offsetHeight + topIndicate
        && offset.top.value < topIndicate;
});

const endFixing = computed(() => {
    if (
        !windowScroll.y 
        || !device.isDesktop.value
        || !imgs.value 
        || !offset.bottom.value
    ) return;

    return offset.bottom.value < imgs.value.offsetHeight + topIndicate;
});

onMounted(() => {
    if (!props.items) return;

    activeItem.value = props.items[0];
    props.items.forEach((item, index) => {
        const el = document.getElementById(item.title);
        if (!el) return;

        useIntersectionObserver(
            el,
            ([entry]) => {
                if (!entry?.isIntersecting) return;
                activeItem.value = props.items[index];
            },
            { rootMargin: '-10% 0px -50% 0px' }
        )
    });
})
</script>

<template>
    <div ref="parent" class="mt-13">
        <div class="w-full flex">
            <div
                ref="imgs"
                class="absolute top-80 inset-x-0"
                :class="[
                    startFixing && !endFixing && 'fixed top-32!',
                    endFixing && 'top-auto! bottom-8!'
                ]"
            >
                <div class="container lg:pr-20!">
                    <Transition name="fade" mode="out-in">
                        <img
                            :src="activeItem?.img"
                            :key="activeItem?.img"
                            class="w-[40%]"
                            :alt="activeItem?.title ?? 'امکانات'"
                            loading="lazy"
                        />
                    </Transition>
                </div>        
            </div>
            <div class="w-[45%] flex flex-col gap-75 mr-auto relative z-1">
                <div
                    v-for="item, index in props.items"
                    :key="item.title"
                    :id="item.title"
                    class="w-full"
                    :class="index === props.items.length - 1 && 'pb-50'"
                >
                    <div class="flex items-center gap-3 mt-30">
                        <div class="w-10 h-10 flex-center bg-secondary-600 rounded-full">
                            <Icon :name="item.icon" size="24" class="text-white" />
                        </div>
                        <h3>{{ item.title }}</h3>
                    </div>
                    <p v-html="item.desc" class="text-body1-semibold text-gray-400 mt-5 max-w-142.5" />
                </div>
            </div>
        </div>
    </div>
</template>